/**
 * LUNA Coding Tutor - Code Editor Component
 * 원본: editor.js의 CodeMirror 관련 로직을 React 컴포넌트로 재구현
 * 
 * 기능:
 * - CodeMirror 6 기반 코드 에디터
 * - Python, JavaScript, HTML, Bash 등 다양한 언어 지원
 * - 빈칸 채우기 모드 (editable-blank) -> Inline Widget으로 변경 (Legacy 복원)
 * - IDE 스타일 테마
 */

import { useState, useEffect, useLayoutEffect, useCallback, useRef, forwardRef, useImperativeHandle, useMemo } from 'react';
import CodeMirror from '@uiw/react-codemirror';
import { python } from '@codemirror/lang-python';
import { javascript } from '@codemirror/lang-javascript';
import { html } from '@codemirror/lang-html';
import { vscodeDark } from '@uiw/codemirror-theme-vscode';
import { EditorView, Decoration, WidgetType, MatchDecorator, ViewPlugin } from '@codemirror/view';
import { Compartment } from '@codemirror/state';
import clsx from 'clsx';

// [Fix] Disable EditContext API to fix IME (Korean/Japanese) input issues in Chrome
// See: https://discuss.codemirror.net/t/japanese-ime-issue/8570
if (typeof EditorView !== 'undefined' && 'EDIT_CONTEXT' in EditorView) {
  EditorView.EDIT_CONTEXT = false;
}

// ============================================
// 유틸리티 함수 (원본 editor.js에서 가져옴)
// ============================================

function formatCodeString(text) {
  if (!text) return '';
  return String(text).replace(/\\n/g, '\n').replace(/\\t/g, '\t');
}

function detectLanguage(code, hintLanguage = 'python') {
  const codeContent = code || '';
  const hasJsKeywords = /\b(function|const|let|var|console\.log|=>)\b/.test(codeContent);
  const hasPyKeywords = /\b(def |print\(|import |from |class .*:)\b/.test(codeContent);
  const hasBashKeywords = /^\s*(cd|ls|pwd|mkdir|rmdir|touch|rm|cp|mv|echo|cat|grep|sudo|chmod|chown|export|source|alias)\b/m.test(codeContent) ||
    codeContent.includes('#!/bin/bash') ||
    codeContent.includes('#!/bin/sh');

  if (hasBashKeywords) {
    return 'bash';
  } else if (hasJsKeywords && !hasPyKeywords && hintLanguage === 'python') {
    return 'javascript';
  } else if (hasPyKeywords && !hasJsKeywords && hintLanguage === 'javascript') {
    return 'python';
  }

  return hintLanguage.toLowerCase();
}

function getFilename(language) {
  const filenameMap = {
    'python': 'main.py',
    'javascript': 'main.js',
    'jsx': 'App.jsx', // [Fix] Add App.jsx
    'html': 'index.html',
    'htmlmixed': 'index.html',
    'bash': 'script.sh',
    'shell': 'script.sh',
    'css': 'style.css',
  };
  return filenameMap[language] || 'main.py';
}

function getLanguageExtension(language) {
  switch (language) {
    case 'jsx': // [Fix] Add JSX support
    case 'javascript':
    case 'js':
      return javascript({ jsx: true });
    case 'html':
    case 'htmlmixed':
      // [Fix] HTML language extension with autoCloseTags disabled for IME compatibility
      return html({ autoCloseTags: false, matchClosingTags: false });
    case 'python':
    default:
      return python();
  }
}

// ============================================
// 에디터 테마 커스터마이징 (Monaco 스타일 통일)
// ============================================

const customTheme = EditorView.theme({
  '&': {
    fontSize: '14px', // [Fix] Match Monaco font size
    fontFamily: "'Fira Code', 'Consolas', monospace",
    backgroundColor: 'transparent !important', // Force transparency over theme
    color: '#e2e8f0',
  },
  '.cm-scroller': {
    backgroundColor: 'transparent !important',
    fontFamily: "'Fira Code', 'Consolas', monospace",
    lineHeight: '1.6', // [Fix] Explicit comfortable line height
  },
  '.cm-content': {
    padding: '12px 0',
    caretColor: '#22d3ee',
  },
  '.cm-line': {
    padding: '0 4px 0 8px', // [Fix] Reduced left padding (was 16px)
  },
  '.cm-gutters': {
    backgroundColor: 'transparent !important',
    borderRight: 'none', // [Fix] Removed vertical divider line
    color: '#64748b',
  },
  '.cm-gutter': {
    minWidth: '32px',
    backgroundColor: 'transparent !important',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
  },
  '.cm-lineNumbers .cm-gutterElement': {
    display: 'flex',
    justifyContent: 'flex-end', // Right align
    paddingRight: '4px', // [Fix] Reduced padding (was 12px) to fix "left-aligned" look
    color: '#64748b',
    cursor: 'default',
  },
  '.cm-activeLineGutter': {
    backgroundColor: 'transparent',
    color: '#94a3b8',
  },
  '.cm-activeLine': {
    backgroundColor: 'rgba(30, 41, 59, 0.5)',
  },
  '.cm-selectionBackground': {
    backgroundColor: 'rgba(51, 65, 85, 0.6) !important',
  },
  '&.cm-focused .cm-cursor': {
    borderLeftColor: '#22d3ee',
  },
  '.cm-tooltip': {
    backgroundColor: '#1e293b',
    border: '1px solid #334155',
    borderRadius: '4px',
  },
  // Inline Input Styles - Modern & Sleek
  '.cm-cloze-input': {
    backgroundColor: 'rgba(255, 255, 255, 0.05)', // Very subtle white tint
    border: '1px solid rgba(255, 255, 255, 0.1)', // Subtle full border for visibility
    borderBottom: '1px solid rgba(255, 255, 255, 0.2)', // Slightly stronger bottom
    borderRadius: '4px',
    color: '#e2e8f0',
    padding: '0 6px',
    minWidth: '60px', /* Slightly wider */
    outline: 'none',
    fontFamily: 'inherit',
    fontSize: 'inherit',
    height: 'auto',
    lineHeight: 'inherit',
    transition: 'all 0.2s ease',
    textAlign: 'center',
    caretColor: '#38bdf8',
    backdropFilter: 'blur(4px)',
  },
  '.cm-cloze-input:focus': {
    backgroundColor: 'rgba(34, 211, 238, 0.1)', // cyan/10
    borderColor: 'rgba(34, 211, 238, 0.3)',
    borderBottomColor: 'rgba(34, 211, 238, 0.6)',
    color: '#fff',
    boxShadow: '0 0 10px rgba(34, 211, 238, 0.1)',
  },
  '.cm-cloze-input::placeholder': {
    color: 'rgba(148, 163, 184, 0.4)',
    fontSize: '0.85em',
    fontStyle: 'italic',
  },
});

// ============================================
// 빈칸 채우기 위젯 (Cloze Widget)
// ============================================

class BlankInputWidget extends WidgetType {
  constructor(id, value, onChange) {
    super();
    this.id = id;
    this.value = value;
    this.onChange = onChange;
  }

  toDOM() {
    const input = document.createElement('input');
    input.className = 'cm-cloze-input';
    input.value = this.value || '';
    input.placeholder = '빈칸'; // or this.id
    input.oninput = (e) => {
      this.onChange(this.id, e.target.value);
    };
    return input;
  }

  updateDOM(dom) {
    if (dom.value !== this.value) {
      dom.value = this.value;
    }
    return true; // Use existing DOM if possible
  }

  eq(other) {
    // [Fix] 값 비교를 제외하여 DOM 재생성 방지 (포커스 유지)
    // updateDOM에서 값 업데이트를 처리함
    return other.id === this.id;
  }
}

function createClozeExtension(blankValues, onBlankChange) {
  const decorator = new MatchDecorator({
    regexp: /__BLANK_\d+__/g,
    decoration: (match) => {
      const id = match[0];
      return Decoration.replace({
        widget: new BlankInputWidget(id, blankValues[id] || '', onBlankChange),
      });
    },
  });

  return ViewPlugin.define(
    (view) => ({
      decorations: decorator.createDeco(view),
      update(update) {
        this.decorations = decorator.updateDeco(update, this.decorations);
      },
    }),
    {
      decorations: (v) => v.decorations,
    }
  );
}


// ============================================
// 메인 CodeEditor 컴포넌트
// ============================================

const CodeEditor = forwardRef(function CodeEditor({
  initialCode = '',
  language = 'python',
  readOnly = false,
  placeholder = '// 코드를 작성해주세요',
  showLineNumbers = true,
  height = '300px',
  onChange,
  onRun,
  className,
  blankMarkers = [],
  isAppliedChallenge = false,
  extensions: propExtensions = [], // 추가 확장 지원
}, ref) {
  const [code, setCode] = useState('');
  const [detectedLanguage, setDetectedLanguage] = useState(language);
  const [filename, setFilename] = useState('main.py');
  const [isComposing, setIsComposing] = useState(false); // [Fix] Track IME composition for dynamic language switching

  const editorViewRef = useRef(null);

  // [Fix] Language compartment for dynamic extension reconfiguration during IME
  const languageCompartmentRef = useRef(new Compartment());

  // [Fix] Use a ref to always access the LATEST initialCode value, avoiding stale closures
  const initialCodeRef = useRef(initialCode);
  useEffect(() => {
    initialCodeRef.current = initialCode;
  }, [initialCode]);

  // [Fix] Sync detectedLanguage with language prop
  useEffect(() => {
    setDetectedLanguage(language);
  }, [language]);

  // [UX] Update filename when language changes
  useEffect(() => {
    setFilename(getFilename(detectedLanguage));
  }, [detectedLanguage]);

  // [Fix] Content GUARD - Restores content if cleared by extension updates
  // This runs SYNCHRONOUSLY after EVERY render to catch when extensions clear content
  // Uses useLayoutEffect to run before browser paint, preventing flicker
  useLayoutEffect(() => {
    if (!editorViewRef.current) return;

    const formattedCode = formatCodeString(initialCodeRef.current);
    if (!formattedCode) return; // Don't guard empty content

    const currentDoc = editorViewRef.current.state.doc.toString();

    // If document is empty but we have content to show, restore it
    if (currentDoc.length === 0 && formattedCode.length > 0) {
      editorViewRef.current.dispatch({
        changes: { from: 0, to: 0, insert: formattedCode }
      });
      setCode(formattedCode);
    }
  }); // No dependencies - runs every render

  // [Fix] Content Update Logic (Uncontrolled Mode)
  // This useEffect handles UPDATES when initialCode prop changes (new task loaded)
  useEffect(() => {
    if (!editorViewRef.current) return;

    const formattedCode = formatCodeString(initialCode);
    const currentDoc = editorViewRef.current.state.doc.toString();

    // Update only if content has changed (new task loaded)
    if (currentDoc !== formattedCode) {
      editorViewRef.current.dispatch({
        changes: { from: 0, to: currentDoc.length, insert: formattedCode }
      });
      // Sync internal state as well
      setCode(formattedCode);
    }
  }, [initialCode]);


  useImperativeHandle(ref, () => ({
    getCode: () => code,
    setCode: (newCode) => {
      const formatted = formatCodeString(newCode);
      setCode(formatted);
      if (editorViewRef.current) {
        const currentDoc = editorViewRef.current.state.doc.toString();
        if (currentDoc !== formatted) {
          editorViewRef.current.dispatch({
            changes: { from: 0, to: currentDoc.length, insert: formatted }
          });
        }
      }
    },
    focus: () => editorViewRef.current?.focus(),
    getEditorView: () => editorViewRef.current,
    getBlankValues: () => ({ code }),
  }));

  // [Fix] Track IME composition state to prevent React updates during Korean/Japanese input
  const isComposingRef = useRef(false);

  const handleCompositionStart = useCallback(() => {
    isComposingRef.current = true;
  }, []);

  const handleCompositionEnd = useCallback(() => {
    isComposingRef.current = false;
    // After composition ends, sync the final state
    if (editorViewRef.current) {
      const finalDoc = editorViewRef.current.state.doc.toString();
      setCode(finalDoc);
      if (onChange) {
        onChange(finalDoc, null);
      }
    }
  }, [onChange]);

  const handleChange = useCallback((value, viewUpdate) => {
    // [Fix] Skip React state updates during IME composition to prevent duplication
    if (isComposingRef.current) {
      return;
    }

    setCode(value);
    if (onChange) {
      onChange(value, viewUpdate);
    }
  }, [onChange]);

  // [Fix] Immediate Initialization using ref-based access
  // We MUST initialize here because useEffect might run before the view was ready.
  // By using initialCodeRef.current, we always get the LATEST value, not a stale closure.
  const handleCreateEditor = useCallback((view) => {
    editorViewRef.current = view;

    // Read from ref to get the latest value, not the captured closure
    const formattedCode = formatCodeString(initialCodeRef.current);
    const currentDoc = view.state.doc.toString();

    if (currentDoc !== formattedCode) {
      view.dispatch({
        changes: { from: 0, to: currentDoc.length, insert: formattedCode }
      });

      // Sync internal state
      setCode(formattedCode);
    }
  }, []); // No dependencies! Uses ref to access latest value

  const handleRun = useCallback(() => {
    if (onRun) {
      onRun(code);
    }
  }, [code, onRun]);

  const handleKeyDown = useCallback((e) => {
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      e.preventDefault();
      handleRun();
    }
  }, [handleRun]);

  // [Fix] CodeMirror-level composition event handling extension
  // This catches IME events directly on the contenteditable, not the React wrapper
  const compositionExtension = useMemo(() => {
    return EditorView.domEventHandlers({
      compositionstart: () => {
        isComposingRef.current = true;

        // [Fix] Dynamically reconfigure to disable HTML language during IME
        const isHtml = detectedLanguage === 'html' || detectedLanguage === 'htmlmixed';
        if (isHtml && editorViewRef.current) {
          editorViewRef.current.dispatch({
            effects: languageCompartmentRef.current.reconfigure([]) // Disable HTML
          });
        }
        return false;
      },
      compositionend: () => {
        isComposingRef.current = false;

        // Sync final state after composition
        setTimeout(() => {
          if (editorViewRef.current) {
            const finalDoc = editorViewRef.current.state.doc.toString();
            setCode(finalDoc);
            if (onChange) {
              onChange(finalDoc, null);
            }

            // [Fix] Dynamically reconfigure to re-enable HTML language
            const isHtml = detectedLanguage === 'html' || detectedLanguage === 'htmlmixed';
            if (isHtml) {
              editorViewRef.current.dispatch({
                effects: languageCompartmentRef.current.reconfigure(html({ autoCloseTags: false, matchClosingTags: false }))
              });
            }
          }
        }, 50);
        return false;
      }
    });
  }, [onChange, detectedLanguage]);

  // [Fix] Extensions now use compartment for language extension
  const extensions = useMemo(() => {
    const langExtension = getLanguageExtension(detectedLanguage);

    return [
      languageCompartmentRef.current.of(langExtension), // Wrap in compartment for dynamic reconfigure
      customTheme,
      EditorView.lineWrapping,
      compositionExtension,
      ...propExtensions,
    ];
  }, [detectedLanguage, compositionExtension, propExtensions]);

  return (
    <div
      className={clsx(
        'code-editor-container rounded-xl overflow-hidden border border-white/5 bg-black/20 backdrop-blur-md',
        className
      )}
      onKeyDown={handleKeyDown}
      onCompositionStart={handleCompositionStart}
      onCompositionEnd={handleCompositionEnd}
    >
      <div className="ide-header flex items-center justify-between px-4 py-2 bg-white/5 border-b border-white/5">
        <div className="flex items-center gap-3">
          <div className="ide-dots flex gap-1.5">
            <span className="ide-dot red w-3 h-3 rounded-full bg-red-500" />
            <span className="ide-dot yellow w-3 h-3 rounded-full bg-yellow-500" />
            <span className="ide-dot green w-3 h-3 rounded-full bg-green-500" />
          </div>
          <span className="ide-title text-xs text-slate-400">{filename}</span>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-500 bg-slate-700/50 px-2 py-1 rounded">
            {detectedLanguage}
          </span>
          {onRun && (
            <button
              onClick={handleRun}
              className="flex items-center gap-1.5 bg-green-600 hover:bg-green-500 text-white text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors"
            >
              <i className="fas fa-play text-[10px]" />
              <span>실행</span>
              <span className="text-green-300/70 text-[10px]">Ctrl+↵</span>
            </button>
          )}
        </div>
      </div>

      <div style={{ height }}>
        <CodeMirror
          // key={initialCode} // [Fix] Removed key to prevent remounting issues. We handle updates via useEffect.
          // value={undefined} // Uncontrolled mode

          onChange={handleChange}
          extensions={extensions}
          theme={vscodeDark}
          placeholder={placeholder}
          readOnly={readOnly && !isAppliedChallenge}
          basicSetup={{
            lineNumbers: showLineNumbers,
            highlightActiveLineGutter: true,
            highlightActiveLine: true,
            foldGutter: true,
            dropCursor: true,
            allowMultipleSelections: true,
            allowMultipleSelections: true,
            indentOnInput: false, // [Fix] IME 입력 중 자동 들여쓰기로 인한 중복 입력 방지
            bracketMatching: true,
            closeBrackets: false, // [Fix] IME 충돌 방지 위해 자동 닫기 비활성화
            autocompletion: true,
            rectangularSelection: true,
            crosshairCursor: true,
            highlightSelectionMatches: true,
            closeBracketsKeymap: true,
            searchKeymap: true,
            foldKeymap: true,
            completionKeymap: true,
            lintKeymap: true,
          }}
          onCreateEditor={handleCreateEditor}
          className="h-full"
        />
      </div>
    </div>
  );
});

// ============================================
// 빈칸 채우기 에디터 컴포넌트
// ============================================

const DEFAULT_BLANK_PATTERN = /__BLANK_\d+__/g;

/**
 * 빈칸 채우기 전용 에디터
 * 코드 템플릿에서 빈칸만 편집 가능
 */
export const FillInBlankEditor = forwardRef(function FillInBlankEditor({
  template = '',
  blankPattern = DEFAULT_BLANK_PATTERN,
  language = 'python',
  onBlankChange,
  onChange, // [New]
  onSubmit, // Optional now, since we expose submit via ref
}, ref) {
  const [blankValues, setBlankValues] = useState({});
  const editorRef = useRef(null);

  // 빈칸 추출 및 초기화
  useEffect(() => {
    const matches = template.match(blankPattern) || [];
    const initialValues = {};
    matches.forEach((marker) => {
      initialValues[marker] = '';
    });
    setBlankValues(initialValues);
  }, [template, blankPattern]);

  // 빈칸 값 변경 (from Widget)
  const handleBlankChange = useCallback((marker, value) => {
    setBlankValues((prev) => ({ ...prev, [marker]: value }));
  }, []);

  // [Fix] 상태 변경 시 부모에게 알림 (Side Effects moved to useEffect)
  useEffect(() => {
    // 1. 전체 코드 생성
    let fullCode = template;
    Object.entries(blankValues).forEach(([m, v]) => {
      fullCode = fullCode.replace(new RegExp(m.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'), v || '');
    });

    // 2. 부모 onChange 호출 (Persistence)
    if (onChange) {
      onChange(fullCode);
    }

    // 3. onBlankChange 호출
    if (onBlankChange) {
      onBlankChange(blankValues);
    }
  }, [blankValues, onChange, onBlankChange, template]);

  // Cloze 확장 생성 (Memoized)
  const clozeExtension = useMemo(() => {
    return createClozeExtension(blankValues, handleBlankChange);
  }, [blankValues, handleBlankChange]);

  const getCompletedCode = useCallback(() => {
    let result = template;
    Object.entries(blankValues).forEach(([marker, value]) => {
      result = result.replace(new RegExp(marker.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'), value);
    });
    return result;
  }, [template, blankValues]);

  // Ref로 포커스, 코드 가져오기, 제출(채점) 메서드 노출
  useImperativeHandle(ref, () => ({
    getCompletedCode,
    submit: () => getCompletedCode(), // Return code on submit
    focus: () => editorRef.current?.focus(),
    setCode: (newCode) => editorRef.current?.setCode(newCode),
  }));

  return (
    <div className="fill-in-blank-editor h-full flex flex-col">
      <CodeEditor
        ref={editorRef}
        initialCode={template}
        language={language}
        readOnly={true} // 텍스트 편집은 불가, 위젯 편집은 가능
        showLineNumbers={true}
        height="100%"
        extensions={[clozeExtension]} // 빈칸 확장 주입
        className="flex-1 border-white/5" // 강조 테두리
      />
    </div>
  );
});

// ============================================
// 출력 패널 컴포넌트
// ============================================

export function OutputPanel({
  output = '',
  isError = false,
  isLoading = false,
  language = 'python',
  renderedHtml = null,
}) {
  return (
    <div className="output-panel rounded-xl overflow-hidden border border-white/5 bg-black/20 backdrop-blur-md">
      <div className="flex items-center justify-between px-4 py-2 bg-white/5 border-b border-white/5">
        <div className="flex items-center gap-2">
          <i className={clsx(
            'text-sm',
            isLoading ? 'fas fa-spinner animate-spin text-cyan-400' :
              isError ? 'fas fa-times-circle text-red-400' :
                'fas fa-terminal text-green-400'
          )} />
          <span className="text-xs text-slate-400">
            {isLoading ? '실행 중...' : '출력'}
          </span>
        </div>
        {!isLoading && output && (
          <button
            onClick={() => navigator.clipboard.writeText(output)}
            className="text-xs text-slate-500 hover:text-slate-300 transition-colors"
          >
            <i className="fas fa-copy mr-1" />
            복사
          </button>
        )}
      </div>

      <div className="p-4 min-h-[100px] max-h-[300px] overflow-auto">
        {renderedHtml ? (
          <iframe
            srcDoc={renderedHtml}
            className="w-full h-[200px] bg-white rounded-lg"
            sandbox="allow-scripts"
            title="출력 미리보기"
          />
        ) : output ? (
          <pre
            className={clsx(
              'text-sm font-mono whitespace-pre-wrap break-words',
              isError ? 'text-red-400' : 'text-green-400'
            )}
          >
            {output}
          </pre>
        ) : !isLoading ? (
          <p className="text-sm text-slate-500 text-center py-8">
            코드를 실행하면 결과가 여기에 표시됩니다
          </p>
        ) : null}
      </div>
    </div>
  );
}

export default CodeEditor;
